import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
	ChangeDetectionStrategy,
	Component,
	DestroyRef,
	OnInit,
	computed,
	inject,
	input,
	output,
	signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
	EMPTY,
	catchError,
	debounceTime,
	distinctUntilChanged,
	finalize,
	map,
	merge,
	of,
	startWith,
	switchMap,
} from 'rxjs';
import { CURRENCIES, DEFAULT_CURRENCY } from '@/common/constants/currencies';
import { toMinorUnits } from '@/_utils/money.util';
import { DriverSearchApiService, DriverSearchResult } from '@/drivers/_api/driver-search.api.service';
import { DriverLedgerApiService } from '@/drivers/driver-ledger/_api/driver-ledger.api.service';
import {
	DocumentNumberCheck,
	DocumentNumberDuplicate,
	DriverLedgerCategoryOption,
} from '@/drivers/driver-ledger/_models/driver-ledger.model';
import { GboxScansApiService } from '@/tms/gbox-scans/_api/gbox-scans.api.service';
import {
	DriverSuggestionReason,
	DriverSuggestionStage,
	Scan,
	SuggestedDriver,
	SuggestedDriversResponse,
	SuggestedDriversVehicle,
	UpdateScanPayload,
} from '@/tms/gbox-scans/_models/gbox-scans.model';

const MINOR_UNITS_FACTOR = 100;
const MAX_DESCRIPTION_LENGTH = 1000;
const MAX_DOCUMENT_NUMBER_LENGTH = 100;
const DRIVER_SEARCH_MIN_CHARS = 2;
const DRIVER_SEARCH_DEBOUNCE_MS = 200;
const DOCUMENT_CHECK_DEBOUNCE_MS = 300;

@Component({
	selector: 'app-scan-ledger-form',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatAutocompleteModule,
		MatButtonModule,
		MatButtonToggleModule,
		MatFormFieldModule,
		MatIconModule,
		MatInputModule,
		MatProgressSpinnerModule,
		MatSelectModule,
		MatTooltipModule,
	],
	templateUrl: './scan-ledger-form.component.html',
	styleUrl: './scan-ledger-form.component.scss',
})
export class ScanLedgerFormComponent implements OnInit {
	readonly scan = input.required<Scan>();
	readonly driverId = input<string | null>(null);
	readonly suggestedAmount = input<number | null>(null);
	readonly suggestedCurrency = input<string | null>(null);

	readonly posted = output<Scan>();
	readonly cancelled = output<void>();

	readonly currencies = CURRENCIES;
	readonly maxDescriptionLength = MAX_DESCRIPTION_LENGTH;
	readonly maxDocumentNumberLength = MAX_DOCUMENT_NUMBER_LENGTH;

	readonly stageLabels: Readonly<Record<DriverSuggestionStage, string>> = {
		departure: 'Dojazd',
		program: 'Program',
		return: 'Powrót',
	};

	readonly form = inject(FormBuilder).nonNullable.group({
		description: new FormControl<string>('', {
			nonNullable: true,
			validators: [Validators.maxLength(MAX_DESCRIPTION_LENGTH)],
		}),
		categoryId: new FormControl<number | null>(null),
		currency: new FormControl<string | null>(DEFAULT_CURRENCY),
		amount: new FormControl<number | null>(null, [Validators.min(0)]),
		documentNumber: new FormControl<string | null>(null, [Validators.maxLength(MAX_DOCUMENT_NUMBER_LENGTH)]),
	});
	readonly ledgerDriver = new FormControl<DriverSearchResult | string | null>(null);
	readonly targetCurrency = new FormControl<'PLN' | 'EUR'>('PLN', { nonNullable: true });

	readonly categories = signal<ReadonlyArray<DriverLedgerCategoryOption>>([]);
	readonly drivers = signal<ReadonlyArray<DriverSearchResult>>([]);
	readonly suggestedDrivers = signal<ReadonlyArray<SuggestedDriver>>([]);
	readonly suggestionVehicle = signal<SuggestedDriversVehicle | null>(null);
	readonly suggestionReason = signal<DriverSuggestionReason | null>(null);
	readonly saving = signal(false);
	readonly error = signal<string | null>(null);
	readonly duplicateEntry = signal<DocumentNumberDuplicate | null>(null);

	readonly hasKnownDriver = computed(() => (this.driverId() ?? '').trim() !== '');

	readonly summary = computed(() => {
		const value = this.formValue();
		const amount = value.amount;
		const currency = value.currency;
		if (amount === null || amount === undefined || amount <= 0 || !currency) {
			return null;
		}
		return { amount, currency, categoryName: this.categoryNameOf(value.categoryId ?? null) };
	});

	private readonly formValue = toSignal(this.form.valueChanges, { initialValue: this.form.getRawValue() });
	private readonly originalUpdatedAt = signal<string | null>(null);

	private readonly scanApi = inject(GboxScansApiService);
	private readonly ledgerApi = inject(DriverLedgerApiService);
	private readonly driverSearch = inject(DriverSearchApiService);
	private readonly destroyRef = inject(DestroyRef);

	ngOnInit(): void {
		const scan = this.scan();
		this.originalUpdatedAt.set(scan.updated_at ?? null);
		this.applyScanToForm(scan);
		this.syncReportingCurrency(this.form.controls.currency.value);
		this.wireReportingCurrency();
		this.loadCategories();
		this.wireDocumentNumberCheck();

		if (!this.hasKnownDriver()) {
			this.loadSuggestedDrivers();
			this.wireDriverSearch();
		}
	}

	displayDriver(driver: DriverSearchResult | string | null): string {
		if (driver === null || driver === undefined) {
			return '';
		}
		if (typeof driver === 'string') {
			return driver;
		}
		return `${driver.name} ${driver.surname}`.trim();
	}

	pickSuggestion(suggestion: SuggestedDriver): void {
		this.ledgerDriver.setValue(this.toDriverSearchResult(suggestion));
		this.drivers.set([]);
	}

	cancel(): void {
		this.cancelled.emit();
	}

	submit(): void {
		if (this.saving()) {
			return;
		}

		const driverId = this.resolveDriverId();
		if (driverId === null) {
			this.error.set('Wybierz kierowcę z listy, aby utworzyć wpis w kasie.');
			return;
		}

		const validationError = this.validateCostFields();
		if (validationError !== null) {
			this.error.set(validationError);
			return;
		}

		this.error.set(null);
		this.saving.set(true);

		const scanId = this.scan().id;
		const targetCurrency = this.targetCurrency.value;

		this.scanApi
			.update(scanId, this.buildUpdatePayload())
			.pipe(
				switchMap((res) => {
					this.originalUpdatedAt.set(res.data.updated_at ?? null);
					return this.scanApi.createDriverLedgerEntry(scanId, {
						driverId,
						targetCurrency,
						documentNumber: this.resolveDocumentNumber(),
					});
				}),
				catchError((err: unknown) => {
					this.error.set(this.resolveError(err));
					return EMPTY;
				}),
				finalize(() => this.saving.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((res) => this.posted.emit(res.data.scan));
	}

	private categoryNameOf(id: number | null): string | null {
		if (id === null) {
			return null;
		}
		return this.categories().find((category) => category.id === id)?.name ?? null;
	}

	private validateCostFields(): string | null {
		const value = this.form.getRawValue();
		if (value.categoryId === null) {
			return 'Kategoria jest wymagana, aby zatwierdzić koszt.';
		}
		if (!value.currency) {
			return 'Waluta jest wymagana, aby zatwierdzić koszt.';
		}
		if (value.amount === null || value.amount <= 0) {
			return 'Kwota musi być większa od zera.';
		}
		return null;
	}

	private wireReportingCurrency(): void {
		this.form.controls.currency.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((currency) => this.syncReportingCurrency(currency));
	}

	private syncReportingCurrency(costCurrency: string | null): void {
		if (!this.targetCurrency.pristine) {
			return;
		}
		this.targetCurrency.setValue(costCurrency === 'EUR' ? 'EUR' : 'PLN');
	}

	private buildUpdatePayload(): UpdateScanPayload {
		const value = this.form.getRawValue();
		return {
			description: value.description.trim() === '' ? null : value.description.trim(),
			categoryId: value.categoryId,
			currency: value.currency || null,
			currencyValue: toMinorUnits(value.amount),
			costStatus: 'confirmed',
			updatedAt: this.originalUpdatedAt(),
			force: false,
		};
	}

	private applyScanToForm(scan: Scan): void {
		const hasAmount = scan.currencyValue !== null && scan.currencyValue !== undefined;
		const amount = hasAmount ? scan.currencyValue! / MINOR_UNITS_FACTOR : this.suggestedAmount();
		const scanCurrency = scan.currency && scan.currency !== '' ? scan.currency : null;
		const scanDescription = (scan.description ?? '').trim();
		this.form.patchValue({
			description: scanDescription !== '' ? scanDescription : (scan.ocrSummary ?? ''),
			categoryId: scan.categoryId,
			currency: scanCurrency ?? this.resolveSuggestedCurrency() ?? DEFAULT_CURRENCY,
			amount,
			documentNumber: scan.documentNumber,
		});
	}

	private wireDocumentNumberCheck(): void {
		merge(this.form.controls.documentNumber.valueChanges, this.ledgerDriver.valueChanges)
			.pipe(
				startWith(null),
				debounceTime(DOCUMENT_CHECK_DEBOUNCE_MS),
				map(() => ({ driverId: this.resolveDriverId(), documentNumber: this.resolveDocumentNumber() })),
				distinctUntilChanged((a, b) => a.driverId === b.driverId && a.documentNumber === b.documentNumber),
				switchMap(({ driverId, documentNumber }) => {
					if (driverId === null || documentNumber === null) {
						return of<DocumentNumberCheck>({ duplicate: false, entry: null });
					}
					return this.ledgerApi
						.checkDocumentNumber(driverId, documentNumber)
						.pipe(catchError(() => of<DocumentNumberCheck>({ duplicate: false, entry: null })));
				}),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((res) => this.duplicateEntry.set(res.duplicate ? res.entry : null));
	}

	private resolveDocumentNumber(): string | null {
		const value = (this.form.controls.documentNumber.value ?? '').trim();
		return value === '' ? null : value;
	}

	private resolveSuggestedCurrency(): string | null {
		const suggested = this.suggestedCurrency();
		if (!suggested) {
			return null;
		}
		return CURRENCIES.some((option) => option.value === suggested) ? suggested : null;
	}

	private wireDriverSearch(): void {
		this.ledgerDriver.valueChanges
			.pipe(
				debounceTime(DRIVER_SEARCH_DEBOUNCE_MS),
				distinctUntilChanged(),
				switchMap((value) => {
					if (typeof value !== 'string' || value.trim().length < DRIVER_SEARCH_MIN_CHARS) {
						return of<DriverSearchResult[]>([]);
					}
					return this.driverSearch.search(value.trim()).pipe(catchError(() => of<DriverSearchResult[]>([])));
				}),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((results) => this.drivers.set(results));
	}

	private loadCategories(): void {
		this.ledgerApi
			.getCategories()
			.pipe(
				catchError(() => EMPTY),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((items) => this.categories.set(items));
	}

	private loadSuggestedDrivers(): void {
		this.scanApi
			.suggestedDrivers(this.scan().id)
			.pipe(
				catchError(() => EMPTY),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((res) => this.applySuggestions(res));
	}

	private applySuggestions(res: SuggestedDriversResponse): void {
		this.suggestionVehicle.set(res.data.vehicle);
		this.suggestionReason.set(res.meta.reason);
		this.suggestedDrivers.set(res.data.suggestions);

		if (res.data.suggestions.length !== 1) {
			return;
		}

		const current = this.ledgerDriver.value;
		const isEmpty = current === null || (typeof current === 'string' && current.trim() === '');
		if (!this.ledgerDriver.pristine || !isEmpty) {
			return;
		}

		this.ledgerDriver.setValue(this.toDriverSearchResult(res.data.suggestions[0]));
	}

	private toDriverSearchResult(suggestion: SuggestedDriver): DriverSearchResult {
		return { id: suggestion.driverId, name: suggestion.name, surname: suggestion.surname };
	}

	private resolveDriverId(): string | null {
		if (this.hasKnownDriver()) {
			return this.driverId();
		}
		const value = this.ledgerDriver.value;
		if (value && typeof value === 'object' && 'id' in value) {
			return value.id;
		}
		return null;
	}

	private resolveError(err: unknown): string {
		if (err instanceof HttpErrorResponse) {
			if (err.status === 409) {
				return 'Skan został zmieniony w innym miejscu — zamknij formularz i otwórz go ponownie, aby pobrać aktualne dane.';
			}
			const body = err.error;
			if (typeof body === 'object' && body !== null) {
				const record = body as Record<string, unknown>;
				if (typeof record['message'] === 'string') {
					return record['message'];
				}
				if (typeof record['detail'] === 'string') {
					return record['detail'];
				}
			}
		}
		return 'Nie udało się utworzyć wpisu w kasie.';
	}
}
